import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { WorkspaceHeader } from "@/components/contracts/WorkspaceHeader";
import { TabDetails } from "@/components/contracts/TabDetails";
import { TabNegotiation } from "@/components/contracts/TabNegotiation";
import { useLang, translateFr as tfr } from "@/i18n";
import { TabSignature } from "@/components/contracts/TabSignature";
import { TabDocuments } from "@/components/contracts/TabDocuments";
import { TabVersions } from "@/components/contracts/TabVersions";
import { TabHistory } from "@/components/contracts/TabHistory";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const TABS = [
  { id: "details", label: tfr("Aperçu") },
  { id: "negotiation", label: tfr("Négociation") },
  { id: "signatories", label: "Signature" },
  { id: "documents", label: "Documents" },
  { id: "history", label: "Historique" },
];
const LEGACY_TABS = { apercu: "details", overview: "details", signature: "signatories", versions: "documents" };

export default function ContractWorkspace() {
  const { id } = useParams();
  const { tf } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const rawTab = searchParams.get("tab");
  const resolved = LEGACY_TABS[rawTab] || rawTab;
  const tab = TABS.some((t) => t.id === resolved) ? resolved : "details";

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/contracts/${id}`);
      setContract(r.data);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "details") next.delete("tab"); else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  const counts = {
    signatories: contract?.signature?.progress?.total || contract?.signatories?.length || 0,
    documents: contract?.documents?.length || 0,
    history: contract?.events?.length || 0,
  };

  if (loading) {
    return <div className="flex items-center justify-center py-32" data-testid="workspace-loading"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  if (notFound || !contract) {
    return (
      <div className="w-full max-w-[900px] mx-auto px-6 pt-16 text-center" data-testid="workspace-not-found">
        <p className="text-lg font-medium">{tfr("Contrat introuvable")}</p>
        <p className="text-sm text-muted-foreground mt-1">{tfr("Ce contrat n'existe pas ou a été supprimé.")}</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 sm:px-6 pt-6 pb-12 space-y-5" data-testid="contract-workspace">
      <WorkspaceHeader contract={contract} onChanged={load} />

      <div role="tablist" aria-label={tfr("Sections du contrat")} className="flex gap-1 overflow-x-auto border-b border-border -mx-1 px-1">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            data-testid={`contract-tab-${t.id}`}
            className={cn("relative px-3 h-11 text-sm whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              tab === t.id ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground")}>
            {tf(t.label)}
            {counts[t.id] > 0 && (
              <span className="ml-1.5 text-xs text-muted-foreground rounded-full bg-secondary/50 px-1.5 py-0.5">{counts[t.id]}</span>
            )}
            {tab === t.id && (
              <motion.span layoutId="contract-tab-underline" className="absolute left-0 right-0 -bottom-px h-0.5 bg-red-500"
                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }} />
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}>
          {tab === "details" && <TabDetails contract={contract} onChanged={load} />}
          {tab === "negotiation" && <TabNegotiation contract={contract} onChanged={load} />}
          {tab === "signatories" && <TabSignature contract={contract} onChanged={load} />}
          {tab === "documents" && (
            <div className="space-y-6">
              <TabDocuments contract={contract} onChanged={load} />
              <TabVersions contract={contract} />
            </div>
          )}
          {tab === "history" && <TabHistory contract={contract} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
